// The terrain pass: the ground of the map, drawn by ONE full-world quad
// (plan Part B, §B.4).
//
// Two different geometries meet here, and each one is built the cheapest way
// that keeps it correct:
//
//  * The **outlines** (floor, rim, body, perimeter) come from the same
//    smoothed field the territory pass uses, at the terrain radius
//    1.8·HEX_SIZE. A field carries holes for free, which is what the
//    reference needs a second traced loop for.
//  * The **insets** (ridge rings, void feathers, void core, and the depth
//    bands of the other looks) need a real distance to the rim, because the
//    field saturates about one hex in and a ring at 0.62 of the depth would
//    sit on a flat. Near the rim the shader blends back to the field, so
//    ring 1 of a small ridge never inherits the raster's staircase.
//  * The **hachures** are anchored on the hex edges, not on the outline:
//    a rim edge of a trench cell carries 2 or 3 ticks whose count, length
//    and position come from a hash of the unordered cell pair, so both
//    sides of the edge agree without either one tracing anything.
//
// **The field and the distance are ONE texture, and this file holds both
// ways to read the field** (R432). `../terrain-prep.ts` computes the plane
// once per hello: R and G are the Euclidean distance transform of the two
// kinds, B and A are the two fields themselves. Terrain is immutable inside
// a world, so there is no flip to answer and no render target to keep.
//
//  - `TERRAIN_TEX_FRAG` reads `fT` and `fR` from the plane with one
//    bilinear tap and takes their gradients from four more. It is the
//    default, and the knob is `#terrain=tex`.
//  - `TERRAIN_FRAG` sums the quartic kernel over the seven cells of ring 1
//    at every screen pixel, which is what the pass did before R432. It is
//    the fallback the layer draws with until the prepare lands, and the
//    knob `#terrain=analytic`.
//
// The two share everything after the field — the picture itself — so a look
// change lands in both.
//
// **The picture is built per LOOK** (R828). A World theme carries one
// `terrain` value, and `./terrain-styles.ts` holds one set of GLSL snippets
// and one uniform list per value. This file is the assembly: every source
// here is a function of the look, and the exported constants are the
// `default` look, which the client compiles exactly as it always did. The
// source builder is the one `./territory.ts` uses for its variants.

import { TERRAIN_EDT_BLEND, TERRAIN_HACHURE_SEED } from '../../gen/presentation';
import { HEX_SIZE, TERRAIN_STYLES } from '../../gen/balance';
import {
  DIST_PLANE_UNIFORMS, PRELUDE, SHARED_UNIFORMS, TERRAIN_RADIUS_K, type UniformSpec,
} from './prelude';
import { worldProgram } from '../gfx/worldquad';
import type { ProgramSource } from './program';
import {
  terrainPaintSrc, terrainStyleDecl, terrainStyleHelpers, terrainStyleUniforms,
} from './terrain-styles';
import {
  terrainBodyWgsl, terrainFragWgsl, terrainTexBodyWgsl, terrainTexFragBodyWgsl,
  terrainTexFragWgsl,
} from './wgsl/terrain';
import type { TerrainStyle } from '../../ui/settings';

/** The look every source here is built for when no caller names one. */
export const TERRAIN_STYLE_DEFAULT = TERRAIN_STYLES[0] as TerrainStyle;

/** The EDT blend band (`[presentation.terrain] edt_blend`): field below the
 *  low figure, EDT above the high one. */
export { TERRAIN_EDT_BLEND };

/**
 * How the two distance channels are read.
 *
 * A bilinear tap of a distance field is piecewise linear, so every iso-line
 * of it is a polygon at the texel pitch. The void core is a stack of
 * iso-lines a few px apart, and it showed those polygons as concentric
 * facets (R431's open list). `1` reads the plane with a cubic B-spline
 * instead, which is C² and has no facet; it costs four bilinear taps in
 * place of one, and only on the pixels that draw a core or a ring. `0` is
 * the old bilinear tap, and the knob `#dtap=bilinear` sets it, so the two
 * can be compared live.
 */
const TERRAIN_DIST_TAP_BSPLINE = 1;

/**
 * How the composite tests for terrain in reach: 0 the scan, 1 the plane.
 *
 * This is the uniform's value before the layer applies the knob, and the
 * scan is what a pass with no knob written draws. The plane is the knob's
 * default since R493, and `#early=fetch` puts the scan back for a
 * measurement (the frame-throughput report of 2026-09-04, package E).
 */
export const TERRAIN_EARLY_PLANE_OFF = 0;

/**
 * The field figure the plane calls "no terrain here".
 *
 * `../terrain-prep.ts` writes B and A only on a texel whose own cell or one
 * of its six neighbours is not open water, AND whose kernel sum over those
 * seven cells is above zero; every other texel keeps the zero the
 * `Float32Array` was allocated with. The two channels are therefore EXACTLY
 * zero wherever the kernel of radius `uRadiusT` reaches no terrain, and a
 * bilinear tap of four such texels is exactly zero as well. Anything above
 * this figure means the tap touched a texel the raster wrote.
 *
 * The two DISTANCE channels cannot answer the same question: the EDT is an
 * inside-distance and it is zero on every texel outside a body, which is
 * exactly where the test must say "no". Only B and A carry the answer.
 */
export const TERRAIN_NEAR_EPS = 1e-6;

/**
 * The uniforms EVERY terrain look declares, whichever picture it draws:
 * where the plane sits, how it is tapped, the one scatter seed, the EDT
 * blend band and the chart's water-lining.
 */
const BASE_UNIFORMS: readonly UniformSpec[] = [
  ...DIST_PLANE_UNIFORMS,
  { name: 'uDistTap', type: 'int', value: TERRAIN_DIST_TAP_BSPLINE },

  // The ONE seed every scatter of this layer reads: the hachure ticks of
  // `default`, the chunk grid of Boulder Field and the dashes of
  // Stratified Wall.
  { name: 'uHachureSeed', type: 'int', value: TERRAIN_HACHURE_SEED },
  { name: 'uEdtBlend', type: 'vec2', value: TERRAIN_EDT_BLEND },

  // The water-lining of card 01 (R548, ui-map-interface.md §17.3.01). Four
  // iso-lines of the ridge distance, on the water OUTSIDE rock, in the
  // historical reading alone. `uWaterLines` 0 is the shipping pixel, and
  // the branch returns before it reads a thing.
  { name: 'uWaterLines', type: 'int', value: 0, dynamic: true },
  { name: 'uLineD', type: 'float[]', value: [0, 0, 0, 0], dynamic: true, note: '4 entries, world px from rock' },
  { name: 'uLineA', type: 'float[]', value: [0, 0, 0, 0], dynamic: true, note: '4 entries' },
  { name: 'uLineW', type: 'float[]', value: [0, 0, 0, 0], dynamic: true, note: '4 entries' },
  { name: 'uLineOut', type: 'float', value: 0, dynamic: true, note: 'the early out, in world px' },
];

/**
 * The analytic pass's own uniforms, on top of `BASE_UNIFORMS`.
 *
 * The layer keeps ONE set of these and a second set per look, so a look
 * change writes back no figure the knobs and the placement own.
 */
export const TERRAIN_BASE_UNIFORMS: readonly UniformSpec[] = [
  ...BASE_UNIFORMS,
  { name: 'uRadiusT', type: 'float', value: TERRAIN_RADIUS_K * HEX_SIZE },
];

/**
 * The composite's own uniforms, on top of `BASE_UNIFORMS`.
 *
 * It has no kernel radius of its own: the plane was rasterised at one, and
 * the shader can no longer disagree with it about what that radius was.
 */
export const TERRAIN_TEX_BASE_UNIFORMS: readonly UniformSpec[] = [
  ...BASE_UNIFORMS,
  { name: 'uEarlyPlane', type: 'int', value: TERRAIN_EARLY_PLANE_OFF },
];

/** Every uniform the analytic program of one look owns, less the shared half. */
function terrainUniforms(
  style: TerrainStyle = TERRAIN_STYLE_DEFAULT,
): readonly UniformSpec[] {
  return [...TERRAIN_BASE_UNIFORMS, ...terrainStyleUniforms(style)];
}

/** Every uniform the composite program of one look owns, less the shared half. */
function terrainTexUniforms(
  style: TerrainStyle = TERRAIN_STYLE_DEFAULT,
): readonly UniformSpec[] {
  return [...TERRAIN_TEX_BASE_UNIFORMS, ...terrainStyleUniforms(style)];
}

/** Every uniform the analytic program of one look declares. */
export function terrainAllUniforms(
  style: TerrainStyle = TERRAIN_STYLE_DEFAULT,
): readonly UniformSpec[] {
  return [...SHARED_UNIFORMS, ...terrainUniforms(style)];
}

/** Every uniform the composite program of one look declares. */
export function terrainTexAllUniforms(
  style: TerrainStyle = TERRAIN_STYLE_DEFAULT,
): readonly UniformSpec[] {
  return [...SHARED_UNIFORMS, ...terrainTexUniforms(style)];
}

/** The analytic pass's own uniforms, for the shipping look. */
export const TERRAIN_UNIFORMS: readonly UniformSpec[] = terrainUniforms();

/** The composite pass's own uniforms, for the shipping look. */
export const TERRAIN_TEX_UNIFORMS: readonly UniformSpec[] = terrainTexUniforms();

/** Every uniform the shipping analytic terrain program declares. */
export const TERRAIN_ALL_UNIFORMS: readonly UniformSpec[] = terrainAllUniforms();

/** Every uniform the shipping composite terrain program declares. */
export const TERRAIN_TEX_ALL_UNIFORMS: readonly UniformSpec[] = terrainTexAllUniforms();

/** The declarations every look shares. */
const BASE_DECL = `
uniform vec2  uDistLo;
uniform vec2  uDistUv;
uniform vec2  uDistStep;
uniform int   uDistTap;

uniform int   uHachureSeed;
uniform vec2  uEdtBlend;

uniform int   uWaterLines;
uniform float uLineD[4];
uniform float uLineA[4];
uniform float uLineW[4];
uniform float uLineOut;
`;

/** Everything every look shares once it has the two fields. */
const BASE_BODY = `
// The world point p as a uv of the terrain plane.
vec2 distUv(vec2 p) { return (p - uDistLo) * uDistUv; }

// ---- the distance channels --------------------------------------------
//
// A cubic B-spline sampled with FOUR bilinear taps (Sigg and Hadwiger). The
// sixteen texels of the 4x4 support are read as four weighted bilinear
// fetches, because every weight of the B-spline basis is positive and each
// axis pair therefore has an interior sample point that carries both of its
// texels. The result is C2, so an iso-line of it is a smooth curve and not
// the polygon a bilinear tap draws (R432, the void core).
vec2 distSpline(vec2 uv) {
  vec2 texel = uDistUv * uDistStep;          // one texel in uv
  vec2 size = 1.0 / texel;                   // the plane in texels
  vec2 coord = uv * size - 0.5;
  vec2 base = floor(coord);
  vec2 f = coord - base;
  vec2 f2 = f * f, f3 = f2 * f;
  vec2 w0 = (1.0 / 6.0) * (-f3 + 3.0 * f2 - 3.0 * f + 1.0);
  vec2 w1 = (1.0 / 6.0) * (3.0 * f3 - 6.0 * f2 + 4.0);
  vec2 w2 = (1.0 / 6.0) * (-3.0 * f3 + 3.0 * f2 + 3.0 * f + 1.0);
  vec2 w3 = (1.0 / 6.0) * f3;
  vec2 s0 = w0 + w1, s1 = w2 + w3;           // the four weights sum to 1
  vec2 t0 = (base + 0.5 + (w1 / s0) - 1.0) * texel;
  vec2 t1 = (base + 0.5 + (w3 / s1) + 1.0) * texel;
  vec2 lo = mix(texture(uDist, vec2(t0.x, t0.y)).rg,
                texture(uDist, vec2(t1.x, t0.y)).rg, s1.x);
  vec2 hi = mix(texture(uDist, vec2(t0.x, t1.y)).rg,
                texture(uDist, vec2(t1.x, t1.y)).rg, s1.x);
  return mix(lo, hi, s1.y);
}

// The two inside-distances at a world point, in world px.
vec2 distAt(vec2 uv) {
  return uDistTap == 1 ? distSpline(uv) : texture(uDist, uv).rg;
}

// ---- the hash every scatter of this layer agrees on --------------------
uint hashU(uint x) {
  x ^= x >> 16; x *= 0x7feb352du;
  x ^= x >> 15; x *= 0x846ca68bu;
  x ^= x >> 16;
  return x;
}
float hash01(uint h) { return float(h & 0x00ffffffu) / 16777216.0; }
`;

/** The early-out every look shares, after the picture. */
const ANY_TERRAIN = `
// Is there any terrain in reach of this pixel?
//
// The two fields read seven cells and no more, and the hachures of an open
// pixel hang off those same six neighbours. When every one of the seven is
// open sea the pass draws nothing at all, so it answers with seven texel
// fetches instead of seven kernels and six edges. Most of the map is open
// sea (R431, the GPU work).
bool anyTerrain(ivec2 c0, int kind0) {
  bool any = kind0 != 0;
  for (int k = 0; k < 6; k++) {
    if (kindOf(neighbor(c0, k)) != 0) any = true;
  }
  return any;
}
`;

/** Everything both programs of ONE look share, after the prelude. */
function commonSrc(style: TerrainStyle): string {
  return BASE_DECL + terrainStyleDecl(style)
    + BASE_BODY + terrainStyleHelpers(style) + terrainPaintSrc(style) + ANY_TERRAIN;
}

// ---------------------------------------------------------------------------
// The analytic pass (the fallback, and `#terrain=analytic`)
// ---------------------------------------------------------------------------

const ANALYTIC = `
uniform float uRadiusT;

void main() {
  vec2 p = vWorld;
  float px = worldPx();
  ivec2 c0 = cellAt(p);
  int kind0 = kindOf(c0);
  if (!anyTerrain(c0, kind0)) { fragColor = vec4(0.0); return; }

  // ---- the two ring-1 fields -------------------------------------------
  float invRT2 = 1.0 / (uRadiusT * uRadiusT);
  float W = 0.0; vec2 G = vec2(0.0);
  float wT = 0.0; vec2 gT = vec2(0.0);
  float wR = 0.0; vec2 gR = vec2(0.0);
  for (int i = 0; i < 7; i++) {
    ivec2 c = (i == 0) ? c0 : neighbor(c0, i - 1);
    float k; vec2 dk;
    kern(p, cellCenter(c), invRT2, k, dk);
    W += k; G += dk;
    int kind = kindOf(c);
    if (kind == 1) { wT += k; gT += dk; }
    else if (kind == 2) { wR += k; gR += dk; }
  }
  if (W < 1e-6) { fragColor = vec4(0.0); return; }

  float fT = wT / W; vec2 dfT = (gT - fT * G) / W;
  float fR = wR / W; vec2 dfR = (gR - fR * G) / W;
  // Each field rises toward its own body, so its gradient points INWARD.
  // The unit form of it is the normal a look copies a body along, and it
  // is taken here because the same division already makes the distance.
  float lT = max(length(dfT), 1e-5);
  float lR = max(length(dfR), 1e-5);
  float sT = (fT - 0.5) / lT;
  float sR = (fR - 0.5) / lR;

  fragColor = terrainPaint(p, c0, kind0, px, sT, sR, dfT / lT, dfR / lR);
}
`;

// ---------------------------------------------------------------------------
// The composite pass (the default)
// ---------------------------------------------------------------------------

const TEXTURED = `
uniform int uEarlyPlane;

// What the plane calls "no terrain in reach". See TERRAIN_NEAR_EPS.
const float NEAR_EPS = float(${TERRAIN_NEAR_EPS});

// The whole pass as ONE function. The cell and the pixel size come in;
// \`main\` below reads them exactly where this pass always read them.
vec4 terrainMain(vec2 p, float px, ivec2 c0, int kind0) {
  // ---- is any terrain in reach? ----------------------------------------
  //
  // Two ways to answer, and the knob picks one (early=plane is the default
  // since R493, the frame-throughput report of 2026-09-04, package E).
  //
  //  * fetch runs the scan: seven texel fetches of the blocked plane.
  //  * plane reads the ONE tap the body needs anyway. Both fields are
  //    exactly zero where the kernel reaches no terrain, so a tap under
  //    NEAR_EPS means the pass would draw nothing: every stroke this pass
  //    paints is gated by the coverage of fT or fR, and both are far below
  //    their 0.5 crossing there.
  //
  // The tap is taken ONCE. Reading it twice would pay the very fetch the
  // knob exists to remove.
  vec2 uv = distUv(p);
  vec4 c;
  if (uEarlyPlane == 1) {
    c = texture(uDist, uv);
    if (max(c.b, c.a) <= NEAR_EPS) { return vec4(0.0); }
  } else {
    if (!anyTerrain(c0, kind0)) { return vec4(0.0); }
    c = texture(uDist, uv);
  }

  // ---- the two fields, read rather than summed --------------------------
  //
  // B is fT and A is fR. The gradient is a central difference over ONE
  // texel of the plane, which is the same construction the territory
  // composite uses; the two fields are one function of position on both
  // sides of their rim, so the difference is continuous across it and the
  // prelude's rule against a screen-space derivative still holds.
  //
  // The tap stays BILINEAR here. The rim is a single iso-line of a field
  // that the raster low-passed over 1.8 hex radii, so a piecewise-linear
  // read of it wobbles by about a twentieth of a world px, and the raster's
  // own 0.5 crossing is what the EDT was aligned to (R432).
  vec2 texel = uDistUv * uDistStep;
  vec2 tx = vec2(texel.x, 0.0), ty = vec2(0.0, texel.y);
  vec4 xp = texture(uDist, uv + tx), xm = texture(uDist, uv - tx);
  vec4 yp = texture(uDist, uv + ty), ym = texture(uDist, uv - ty);

  vec2 dfT = vec2((xp.b - xm.b) / (2.0 * uDistStep.x),
                  (yp.b - ym.b) / (2.0 * uDistStep.y));
  vec2 dfR = vec2((xp.a - xm.a) / (2.0 * uDistStep.x),
                  (yp.a - ym.a) / (2.0 * uDistStep.y));
  float lT = max(length(dfT), 1e-5);
  float lR = max(length(dfR), 1e-5);
  float sT = (c.b - 0.5) / lT;
  float sR = (c.a - 0.5) / lR;

  return terrainPaint(p, c0, kind0, px, sT, sR, dfT / lT, dfR / lR);
}
`;

/** The composite's own `main`, which the FUSED pass replaces (package L). */
const TEXTURED_MAIN = `
void main() {
  vec2 p = vWorld;
  float px = worldPx();
  ivec2 c0 = cellAt(p);
  fragColor = terrainMain(p, px, c0, kindOf(c0));
}
`;

/** The analytic terrain fragment shader of one look, prelude included. */
export function terrainFrag(style: TerrainStyle = TERRAIN_STYLE_DEFAULT): string {
  return PRELUDE + commonSrc(style) + ANALYTIC;
}

/**
 * Everything the composite of one look declares EXCEPT its `main`: the
 * look's uniforms, the shared picture and `terrainMain`. The composite
 * below adds its own `main`.
 */
export function terrainTexBody(style: TerrainStyle = TERRAIN_STYLE_DEFAULT): string {
  return commonSrc(style) + TEXTURED;
}

/** The composite terrain fragment shader of one look, prelude included. */
export function terrainTexFrag(style: TerrainStyle = TERRAIN_STYLE_DEFAULT): string {
  return PRELUDE + terrainTexBody(style) + TEXTURED_MAIN;
}

/** The shipping look's analytic fragment shader. */
export const TERRAIN_FRAG = terrainFrag();

/** The shipping look's composite body, without its `main`. */
export const TERRAIN_TEX_BODY = terrainTexBody();

/** The shipping look's composite fragment shader. */
export const TERRAIN_TEX_FRAG = terrainTexFrag();

// The same three sources as WGSL. They stand HERE and not in
// `./wgsl/terrain.ts`, because the two modules are a cycle — the twins read
// the figures and the uniform lists of this file, and this file fills the
// `wgsl` field of its two programs — and a const of the twin module would
// then be read before it was set. Every piece of the twin is a function for
// the same reason. The constants are the `default` look, to the byte, the
// way the three above are.

/** The shipping look's analytic WGSL module. */
export const TERRAIN_FRAG_WGSL = terrainFragWgsl();

/** The shipping look's composite WGSL body, without its entry point. */
export const TERRAIN_TEX_BODY_WGSL = terrainTexBodyWgsl();

/** The shipping look's composite WGSL module. */
export const TERRAIN_TEX_FRAG_WGSL = terrainTexFragWgsl();

/** The analytic terrain pass of one look, as both backends read it. */
export function terrainProgram(
  style: TerrainStyle = TERRAIN_STYLE_DEFAULT,
): ProgramSource {
  return worldProgram({
    name: style === TERRAIN_STYLE_DEFAULT ? 'terrain' : `terrain+${style}`,
    fragment: terrainFrag(style),
    wgslBody: terrainBodyWgsl(style),
    raw: true,
    uniforms: terrainAllUniforms(style),
  });
}

/** The terrain composite of one look, as both backends read it. */
export function terrainTexProgram(
  style: TerrainStyle = TERRAIN_STYLE_DEFAULT,
): ProgramSource {
  return worldProgram({
    name: style === TERRAIN_STYLE_DEFAULT ? 'terrain-tex' : `terrain-tex+${style}`,
    fragment: terrainTexFrag(style),
    wgslBody: terrainTexFragBodyWgsl(style),
    raw: true,
    uniforms: terrainTexAllUniforms(style),
  });
}
